import { arraySearch, IFormContext } from 'rewire-ui';
import { FieldFilter } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { DefaultSchema } from '../settings/SettingsModel';
import { getNameSearcher } from '../utility/Searchers';
import DocumentDialogModel from './DocumentDialogModel';

export const TimeOffTypeSchema = `
  ${DefaultSchema}
  trackingUnits
  trackingAmounts
  withdrawalReasons {
    id
    name
  }
  accrualReasons {
    id
    name
  }
  start,
  expiry
`;

const withdrawalReasonFilter: FieldFilter = {
  field: 'category',
  in: ['absence', 'premium'],
};

const accrualReasonFilter: FieldFilter = {
  field: 'category',
  in: ['worked', 'premium'],
};

const withdrawalReasonSearchReturnSchema = `
  id
  name
`;

const accrualReasonSearchReturnSchema = `
  id
  name
`;

function validateMonthDay(value: string) {
  return false;
}

const withdrawalReasonSearcher = getNameSearcher('reasons', () => withdrawalReasonFilter, withdrawalReasonSearchReturnSchema);
const accrualReasonSearcher = getNameSearcher('reasons', () => accrualReasonFilter, accrualReasonSearchReturnSchema);

const startOptions = arraySearch(['Month/Day', 'Earned Date', 'Seniority Date'], (item?: any) => item ?? '');
const expiryOptions = arraySearch(['x Years After Start', 'Never Expires'], (item?: any) => item ?? '');
const seniorityOptions = arraySearch(['Facility', 'Vacation', 'Employee', 'Department', 'Line'], (item?: any) => item ?? '');

const seniorityMap: Record<string, string> = {
  "'facilitySeniorityDate:d'": 'Facility',
  "'vacationSeniorityDate:d'": 'Vacation',
  "'seniorityDate:d'": 'Employee',
  "'departmentSeniorityDate:d'": 'Department',
  "'lineSeniorityDate:d'": 'Line',
};

export class TimeOffTypeDialogModel extends DocumentDialogModel {
  constructor() {
    super(
      'Time Off Type',
      (ids: string[]) => DataQueries.getTimeOffTypesByIds(TimeOffTypeSchema, ids),
      DataMutations.insertTimeOffTypes,
      DataMutations.updateTimeOffTypes,
      DataMutations.deleteTimeOffTypes,
      'timeofftypes',
      () => mainPageModel.canAddTimeOffTypes,
      () => mainPageModel.canEditTimeOffTypes,
      () => mainPageModel.canDeleteTimeOffTypes,
    );
  }

  protected otherFields(form: IFormContext) {
    return {
      trackingAmounts: form.switch().label('Track Amounts'),
      trackingUnits: form.switch().label('Track Units'),
      withdrawalReasons: form
        .multiselectautocomplete(withdrawalReasonSearcher, { chipLimit: 3, inputAdd: (v: string) => v })
        .label('Withdrawal Reasons')
        .placeholder('enter withdrawal reasons'),
      accrualReasons: form
        .multiselectautocomplete(accrualReasonSearcher, { chipLimit: 3, inputAdd: (v: string) => v })
        .label('Accrual Reasons')
        .placeholder('enter accrual reasons'),

      start: form.select(startOptions).label('Start').placeholder('select start'),
      monthDay: form
        .mask({ mask: [/[0-1]/, /\d/, '-', /[0-3]/, /\d/] })
        .label('Month/Day')
        .validators(validateMonthDay)
        .updateOnChange(true)
        .placeholder('MM-DD'),
      seniority: form.select(seniorityOptions).label('Seniority').placeholder('select seniority'),
      expiry: form.select(expiryOptions).label('Expires').placeholder('select expiry'),
      years: form.number().label('Years').placeholder('enter years'),
    };
  }

  mapToSave(): ObjectType {
    const obj = super.mapToSave();
    obj.withdrawalReasonIds = obj.withdrawalReasons?.map((r: any) => r.id);
    obj.accrualReasonIds = obj.accrualReasons?.map((r: any) => r.id);
    delete obj.withdrawalReasons;
    delete obj.accrualReasons;
    switch (obj.start) {
      case 'Seniority Date':
        obj.start = `timeoff_type_seniority_of($1,$2,${Object.entries(seniorityMap)
          .filter(([_, v]) => v === obj.seniority)
          .map(([k]) => k)})`;
        break;
      case 'Month/Day':
        {
          // strip preceding 0 from string
          const monthDay = obj.monthDay?.replace(/0(\d)/g, '$1');
          obj.start = `timeoff_type_trunc_to_month_day($1,${monthDay ? monthDay.replace('-', ',') : '1,1'})`;
        }
        break;
      case 'Earned Date':
        obj.start = 'timeoff_type_earned_date($1)';
        break;
    }
    switch (obj.expiry) {
      case 'x Years After Start':
        obj.expiry = `timeoff_type_anniversary_of($1,${obj.years === undefined ? 1 : obj.years})`;
        break;
      case 'Never Expires':
        obj.expiry = 'timeoff_type_never_expires($1)';
        break;
    }
    delete obj.monthDay;
    delete obj.seniority;
    delete obj.years;
    return obj;
  }

  mapToForm(obj: ObjectType) {
    const o = super.mapToForm(obj);
    const start = obj?.start as string | undefined;
    if (start) {
      const m = start.match(/\(([^)]*)\)/);
      const args = m?.[1]?.split(',') ?? [];
      if (start.startsWith('timeoff_type_seniority_of')) {
        o.start = 'Seniority Date';
        o.seniority = seniorityMap[args[2]?.trim()];
      }
      if (start.startsWith('timeoff_type_trunc_to_month_day')) {
        o.start = 'Month/Day';
        o.monthDay = `${args[1]?.trim().padStart(2, '0') ?? '01'}-${args[2]?.trim().padStart(2, '0') ?? '01'}`;
      }
      if (start.startsWith('timeoff_type_earned_date')) {
        o.start = 'Earned Date';
      }
    }

    const expiry = obj?.expiry;
    if (expiry) {
      const m = expiry.match(/\(([^)]*)\)/);
      const args = m?.[1]?.split(',') ?? [];
      if (expiry.startsWith('timeoff_type_anniversary_of')) {
        o.expiry = 'x Years After Start';
        o.years = Number.parseInt(args?.[1].trim(), 10) ?? 1;
      }
      if (expiry.startsWith('timeoff_type_never_expires')) {
        o.expiry = 'Never Expires';
      }
    }
    return o;
  }

  _reset() {
    super._reset({ trackingAmounts: true, trackingUnits: true });
  }
}

const timeOffTypeDialogModel = new TimeOffTypeDialogModel();
export default timeOffTypeDialogModel;
